<section class="flex h-full w-full flex-col overflow-hidden bg-white text-slate-900" role="dialog">
  <header class="flex shrink-0 items-center gap-3 border-b !border-slate-100 px-4 py-3">
    <div class="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600">
      <app-icon>local_shipping</app-icon>
    </div>
    <div class="flex min-w-0 flex-1 flex-col">
      <h2 class="min-w-0 truncate !m-0 !text-base !font-bold !leading-6">{{ 'novaposhta.form.title' | translate }}</h2>
      <span class="truncate text-xs text-slate-400">{{ 'novaposhta.form.subtitle' | translate }}</span>
    </div>
    <app-button btnType="icon" [ariaLabel]="'close' | translate" (onChangeClick)="close.emit()">
      <app-icon>close</app-icon>
    </app-button>
  </header>

  @if (loading) {
    <div class="flex flex-1 flex-col gap-4 bg-slate-50 px-4 py-4">
      <div class="h-32 animate-pulse rounded-lg bg-slate-200/70"></div>
      <div class="h-56 animate-pulse rounded-lg bg-slate-200/70"></div>
      <div class="h-40 animate-pulse rounded-lg bg-slate-200/70"></div>
    </div>
  } @else if (!cabinets.length) {
    <div class="flex flex-1 flex-col items-center justify-center gap-3 bg-slate-50 px-8 text-center">
      <div class="grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-500">
        <app-icon>key</app-icon>
      </div>
      <span class="text-sm font-semibold text-slate-900">{{ 'novaposhta.settings.empty_title' | translate }}</span>
      <span class="max-w-xs text-sm text-slate-500">{{ 'novaposhta.settings.empty_text' | translate }}</span>
      <app-button [customClass]="'!h-9 !rounded-lg'" (onChangeClick)="openSettings()">
        {{ 'novaposhta.settings.add' | translate }}
      </app-button>
    </div>
  } @else {
    <div class="crm-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-slate-50 px-4 py-4">
      <!-- Sender -->
      <section class="flex flex-col gap-3 rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
        <ng-container [ngTemplateOutlet]="sectionTitle" [ngTemplateOutletContext]="{ icon: 'outbox', title: 'novaposhta.form.sender' }" />

        @if (cabinets.length > 1) {
          <app-select
            [formControl]="form.controls.cabinet_id"
            [options]="cabinets"
            optionLabel="name"
            optionValue="id"
            [required]="true"
            [label]="'novaposhta.form.cabinet' | translate"
          />
        }

        @if (cabinet?.sender; as sender) {
          <div class="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
            <div class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-slate-500 shadow-sm">
              <app-icon class="!h-5 !w-5 !text-[20px]">storefront</app-icon>
            </div>
            <div class="flex min-w-0 flex-1 flex-col">
              <span class="truncate text-sm font-semibold text-slate-900">{{ sender.name }}</span>
              <span class="truncate text-xs text-slate-500">
                {{ sender.contact_name }} · {{ formatNpPhone(sender.phone) }}
                @if (cabinets.length === 1) {
                  · {{ cabinet?.name }}
                }
              </span>
            </div>
            <app-button btnType="icon" [ariaLabel]="'novaposhta.form.cabinets' | translate" [matTooltip]="'novaposhta.form.cabinets' | translate" (onChangeClick)="openSettings()">
              <app-icon class="text-slate-400">settings</app-icon>
            </app-button>
          </div>
        }

        @if (cabinet?.sender?.city; as senderCity) {
          <app-select
            [formControl]="form.controls.sender_warehouse_ref"
            [options]="senderWarehouseOptions"
            optionLabel="name"
            optionValue="ref"
            [filter]="true"
            [required]="true"
            [label]="('novaposhta.form.sender_warehouse' | translate) + ' — ' + senderCity.name"
            [placeholder]="'novaposhta.form.warehouse_placeholder' | translate"
            (filterChange)="onSenderWarehouseSearch($event)"
            (valueChange)="onSenderWarehousePicked($event)"
          />
        } @else {
          <div class="flex items-center gap-3 rounded-lg border !border-amber-200 bg-amber-50 px-3 py-2.5">
            <app-icon class="shrink-0 text-amber-600">warning</app-icon>
            <span class="min-w-0 flex-1 text-sm text-amber-800">{{ 'novaposhta.errors.NP_SENDER_NOT_SET' | translate }}</span>
            <app-button btnType="stroked" [customClass]="'!h-8 !rounded-lg !text-sm'" (onChangeClick)="openSettings()">
              {{ 'novaposhta.form.set_send_from' | translate }}
            </app-button>
          </div>
        }
      </section>

      <!-- Recipient -->
      <section class="flex flex-col gap-3 rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
        <ng-container [ngTemplateOutlet]="sectionTitle" [ngTemplateOutletContext]="{ icon: 'person_pin_circle', title: 'novaposhta.form.recipient', hint: prefill ? 'novaposhta.form.prefilled' : null }" />

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <app-input
            [formControl]="recipientForm.controls.name"
            [label]="'novaposhta.form.recipient_name' | translate"
            [required]="true"
            [placeholder]="'novaposhta.form.recipient_name_placeholder' | translate"
          />
          <app-input
            [formControl]="recipientForm.controls.phone"
            [label]="'novaposhta.form.recipient_phone' | translate"
            [required]="true"
            [pKeyFilter]="phoneKeys"
            placeholder="+380XXXXXXXXX"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.form.delivery_type' | translate }}</span>
          <div class="flex flex-wrap gap-2">
            @for (option of deliveryTypes; track option.value) {
              <ng-container [ngTemplateOutlet]="choice" [ngTemplateOutletContext]="{ control: recipientForm.controls.delivery_type, option: option }" />
            }
          </div>
        </div>

        <app-select
          [formControl]="recipientForm.controls.city_ref"
          [options]="cityOptions"
          optionLabel="label"
          optionValue="ref"
          [filter]="true"
          [loading]="citiesLoading"
          [required]="true"
          [label]="'novaposhta.form.city' | translate"
          [placeholder]="'novaposhta.form.city_placeholder' | translate"
          [emptyMessage]="'novaposhta.form.city_search_hint' | translate"
          [emptyFilterMessage]="'novaposhta.form.nothing_found' | translate"
          (filterChange)="onCitySearch($event)"
          (valueChange)="onCityPicked($event)"
        />

        @if (deliveryType === 'address') {
          <div class="grid grid-cols-[minmax(0,1fr)_7rem_6rem] gap-3">
            <app-input
              [formControl]="recipientForm.controls.street"
              [label]="'novaposhta.form.street' | translate"
              [required]="true"
              [placeholder]="'novaposhta.form.street_placeholder' | translate"
            />
            <app-input [formControl]="recipientForm.controls.building" [label]="'novaposhta.form.building' | translate" [required]="true" />
            <app-input [formControl]="recipientForm.controls.flat" [label]="'novaposhta.form.flat' | translate" />
          </div>
        } @else {
          <app-select
            [formControl]="recipientForm.controls.warehouse_ref"
            [options]="warehouseOptions"
            optionLabel="name"
            optionValue="ref"
            [filter]="true"
            [loading]="warehousesLoading"
            [required]="true"
            [label]="(deliveryType === 'postomat' ? 'novaposhta.form.postomat' : 'novaposhta.form.warehouse') | translate"
            [placeholder]="(recipientForm.controls.city_ref.value ? 'novaposhta.form.warehouse_placeholder' : 'novaposhta.form.pick_city_first') | translate"
            [emptyFilterMessage]="'novaposhta.form.nothing_found' | translate"
            (filterChange)="onWarehouseSearch($event)"
            (valueChange)="onWarehousePicked($event)"
          />
        }
      </section>

      <!-- Cargo -->
      <section class="flex flex-col gap-3 rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
        <ng-container [ngTemplateOutlet]="sectionTitle" [ngTemplateOutletContext]="{ icon: 'package_2', title: 'novaposhta.form.cargo' }" />

        <div class="flex flex-wrap gap-2">
          @for (option of cargoTypes; track option.value) {
            <ng-container
              [ngTemplateOutlet]="choice"
              [ngTemplateOutletContext]="{ control: cargoForm.controls.type, option: option, disabled: !isCargoTypeAllowed(option.value) }"
            />
          }
        </div>
        @if (deliveryType === 'postomat') {
          <p class="!m-0 flex items-center gap-1.5 text-xs text-slate-500">
            <app-icon class="!h-4 !w-4 !text-[16px] text-slate-400">info</app-icon>
            {{ 'novaposhta.form.postomat_limits' | translate: { weight: postomatMaxWeight } }}
          </p>
        }

        <app-input
          [formControl]="cargoForm.controls.description"
          [label]="'novaposhta.form.description' | translate"
          [required]="true"
          [maxlength]="descriptionMaxLength"
          [placeholder]="'novaposhta.form.description_placeholder' | translate"
        />

        <div class="grid grid-cols-3 gap-3">
          <app-input
            inputType="number"
            [formControl]="cargoForm.controls.declared_cost"
            [label]="'novaposhta.form.declared_cost' | translate"
            [required]="true"
            [min]="1"
            placeholder="₴"
          />
          <app-input
            inputType="number"
            [formControl]="cargoForm.controls.weight"
            [label]="'novaposhta.form.weight' | translate"
            [required]="true"
            [min]="0.1"
            [invalid]="postomatOverweight"
          />
          <app-input
            inputType="number"
            [formControl]="cargoForm.controls.seats"
            [label]="'novaposhta.form.seats' | translate"
            [required]="true"
            [min]="1"
          />
        </div>
        @if (postomatOverweight) {
          <p class="!m-0 text-xs text-rose-600">{{ 'novaposhta.form.postomat_overweight' | translate: { weight: postomatMaxWeight } }}</p>
        }

        @if (cargoType !== 'Documents') {
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-slate-700">
              {{ 'novaposhta.form.size' | translate }}@if (sizeRequired) {<span>*</span>}
            </span>
            <div class="grid grid-cols-3 gap-3">
              <app-input inputType="number" [formControl]="cargoForm.controls.length" [placeholder]="'novaposhta.form.length' | translate" [min]="1" />
              <app-input inputType="number" [formControl]="cargoForm.controls.width" [placeholder]="'novaposhta.form.width' | translate" [min]="1" />
              <app-input inputType="number" [formControl]="cargoForm.controls.height" [placeholder]="'novaposhta.form.height' | translate" [min]="1" />
            </div>
            @if (sizeRequired) {
              <span class="text-xs text-slate-500">{{ 'novaposhta.form.size_required_postomat' | translate }}</span>
            }
            @if (volumetricWeight) {
              <span class="text-xs text-slate-500">{{ 'novaposhta.form.volumetric_weight' | translate: { weight: volumetricWeight } }}</span>
            }
          </div>
        }
      </section>

      <!-- Payment -->
      <section class="flex flex-col gap-3 rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
        <ng-container [ngTemplateOutlet]="sectionTitle" [ngTemplateOutletContext]="{ icon: 'payments', title: 'novaposhta.form.payment' }" />

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.form.payer' | translate }}</span>
            <div class="flex gap-2">
              @for (option of payers; track option.value) {
                <ng-container [ngTemplateOutlet]="choice" [ngTemplateOutletContext]="{ control: form.controls.payer, option: option }" />
              }
            </div>
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.form.payment_method' | translate }}</span>
            <div class="flex gap-2">
              @for (option of paymentMethods; track option.value) {
                <ng-container
                  [ngTemplateOutlet]="choice"
                  [ngTemplateOutletContext]="{ control: form.controls.payment_method, option: option, disabled: !isPaymentMethodAllowed(option.value) }"
                />
              }
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-slate-700">{{ 'novaposhta.form.cod' | translate }}</span>
          <div class="flex flex-wrap gap-2">
            @for (option of codTypes; track option.value) {
              <ng-container
                [ngTemplateOutlet]="choice"
                [ngTemplateOutletContext]="{ control: form.controls.cod_type, option: option, disabled: !isCodTypeAllowed(option.value) }"
              />
            }
          </div>
        </div>
        @if (isPrivateSender) {
          <ng-container [ngTemplateOutlet]="privatePersonNote" />
        }
        @if (codType !== 'none') {
          <div class="grid grid-cols-1 items-start gap-3 rounded-lg bg-slate-50 p-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
            <app-input
              inputType="number"
              [formControl]="form.controls.cod_amount"
              [label]="'novaposhta.form.cod_amount' | translate"
              [required]="true"
              [min]="1"
              placeholder="₴"
            />
            <p class="!m-0 text-xs leading-5 text-slate-500 sm:pt-7">
              {{ 'novaposhta.form.cod_hint_' + codType | translate }}
            </p>
          </div>
        }
      </section>

      <!-- Extra -->
      <section class="flex flex-col gap-3 rounded-lg border !border-slate-200 bg-white p-4 shadow-sm">
        <ng-container [ngTemplateOutlet]="sectionTitle" [ngTemplateOutletContext]="{ icon: 'tune', title: 'novaposhta.form.extra' }" />
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
          <div class="flex flex-col">
            <label class="label" for="np-send-date">{{ 'novaposhta.form.send_date' | translate }}</label>
            <p-datepicker
              inputId="np-send-date"
              styleClass="w-full"
              [fluid]="true"
              dateFormat="dd.mm.yy"
              [showIcon]="true"
              [minDate]="today"
              [appendTo]="'body'"
              [formControl]="form.controls.send_date"
            />
          </div>
          <app-input
            [formControl]="form.controls.note"
            [label]="'novaposhta.form.note' | translate"
            [maxlength]="noteMaxLength"
            [placeholder]="'novaposhta.form.note_placeholder' | translate"
          />
        </div>
        <p class="!m-0 flex items-center gap-1.5 text-xs text-slate-500">
          <app-icon class="!h-4 !w-4 !text-[16px] text-slate-400">sync_alt</app-icon>
          <span [innerHTML]="'novaposhta.form.ttn_field_hint' | translate"></span>
        </p>
      </section>
    </div>

    <footer class="flex shrink-0 flex-col gap-3 border-t !border-slate-100 px-4 py-3">
      <div class="flex min-h-10 items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
        <app-icon class="shrink-0 text-slate-400">calculate</app-icon>
        @if (estimating) {
          <span class="text-slate-400">{{ 'novaposhta.form.estimating' | translate }}</span>
        } @else if (estimateError) {
          <span class="text-rose-600" [innerHTML]="estimateError"></span>
        } @else if (estimate; as cost) {
          <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-slate-500">
              {{ 'novaposhta.form.delivery_cost' | translate }}
              <b class="text-slate-900">≈ {{ formatMoneyAmount(cost.delivery_cost, 'UAH') }}</b>
            </span>
            @if (cost.cod_cost) {
              <span class="text-slate-500">
                {{ 'novaposhta.form.cod_cost' | translate }}
                <b class="text-slate-900">≈ {{ formatMoneyAmount(cost.cod_cost, 'UAH') }}</b>
              </span>
            }
            @if (cost.estimated_delivery_date) {
              <span class="text-slate-500">
                {{ 'novaposhta.form.delivery_date' | translate }}
                <b class="text-slate-900">{{ cost.estimated_delivery_date | date: 'dd.MM' }}</b>
              </span>
            }
          </div>
        } @else {
          <span class="text-slate-400">{{ 'novaposhta.form.estimate_hint' | translate }}</span>
        }
      </div>
      <div class="flex gap-2">
        <app-button btnType="stroked" class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" (onChangeClick)="close.emit()">
          {{ 'cancel' | translate }}
        </app-button>
        <app-button class="flex-1" [customClass]="'w-full !h-10 !rounded-lg'" [disabled]="saving" (onChangeClick)="save()">
          @if (saving) {
            <span class="flex items-center justify-center gap-2">
              <app-icon class="!h-4 !w-4 animate-spin !text-[16px] !leading-none">progress_activity</app-icon>
              {{ 'novaposhta.form.creating' | translate }}
            </span>
          } @else {
            {{ 'novaposhta.form.create' | translate }}
          }
        </app-button>
      </div>
    </footer>
  }
</section>

<ng-template #sectionTitle let-icon="icon" let-title="title" let-hint="hint">
  <div class="flex items-center gap-2">
    <app-icon class="!h-5 !w-5 !text-[20px] text-slate-400">{{ icon }}</app-icon>
    <h3 class="!m-0 !text-sm !font-semibold !leading-5 text-slate-900">{{ title | translate }}</h3>
    @if (hint) {
      <span class="ml-auto flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
        <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">auto_awesome</app-icon>
        {{ hint | translate }}
      </span>
    }
  </div>
</ng-template>

<ng-template #choice let-control="control" let-option="option" let-disabled="disabled">
  <app-button
    btnType="stroked"
    class="min-w-fit flex-1"
    [disabled]="disabled"
    [customClass]="
      'w-full !h-9 !rounded-lg !px-3 !text-sm ' +
      (control.value === option.value ? '!border-orange-500 !bg-orange-50 !text-orange-700' : '!border-slate-200 !text-slate-600')
    "
    (onChangeClick)="setOption(control, option.value)"
  >
    <span class="flex items-center justify-center gap-1.5 whitespace-nowrap">
      @if (option.icon) {
        <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">{{ option.icon }}</app-icon>
      }
      {{ option.label | translate }}
    </span>
  </app-button>
</ng-template>

<ng-template #privatePersonNote>
  <div class="flex gap-2 rounded-lg border !border-sky-100 bg-sky-50 px-3 py-2.5 text-xs leading-5 text-sky-800">
    <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] text-sky-500">info</app-icon>
    <span>{{ 'novaposhta.private_person_limits' | translate }}</span>
  </div>
</ng-template>
